import React, { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { useData } from '../../../contexts/DataContext';
import { getDisplayName } from '../../../utils/getDisplayName';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    backgroundColor: theme.palette.common.white,
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

// Mobile expandable row component
const MobileRow = ({ row, rank, displayName }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <StyledTableRow
        onClick={() => setOpen(!open)}
        sx={{ cursor: 'pointer' }}
      >
        <StyledTableCell align="left" sx={{ fontWeight: 600, width: '30px', padding: '8px' }}>
          {rank}
        </StyledTableCell>
        <StyledTableCell align="left" sx={{ padding: '8px' }}>
          {getDisplayName(displayName, row.name, { truncate: 18 })}
        </StyledTableCell>
        <StyledTableCell align="right" sx={{ fontWeight: 700, padding: '8px' }}>
          {row.totalPoints}
        </StyledTableCell>
        <StyledTableCell align="right" sx={{ padding: '4px', width: '30px' }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </StyledTableCell>
      </StyledTableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ padding: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <div style={{
              padding: '8px 16px 12px',
              display: 'flex',
              justifyContent: 'space-around',
              fontSize: '12px',
              color: '#666',
              backgroundColor: '#fafafa',
            }}>
              <span>Score Pts: <strong style={{ color: '#333' }}>{row.pickScorePoints}</strong></span>
              <span>Winner Pts: <strong style={{ color: '#333' }}>{row.pickWinnerPoints}</strong></span>
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const LeaderboardTable = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getLeaderboard, userDisplayNames } = useData();
  const [isLoading, setIsLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('totalPoints');

  useEffect(() => {
    fetchLeaderboard();
  }, [props.season, getLeaderboard]);

  const fetchLeaderboard = () => {
    setIsLoading(true);
    getLeaderboard(props.season).then((response) => {
      const newLeaderboard = response.map(doc => doc.data());
      setLeaderboard(newLeaderboard);
      setIsLoading(false);
    }).catch((error) => {
      console.error('Error fetching leaderboard:', error);
      setIsLoading(false);
    });
  }

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRows = leaderboard.slice().sort((a, b) => {
    if (orderBy) {
      const valueA = orderBy === 'name' ? a[orderBy] : parseFloat(a[orderBy]);
      const valueB = orderBy === 'name' ? b[orderBy] : parseFloat(b[orderBy]);

      if (!isNaN(valueA) && !isNaN(valueB)) {
        return order === 'asc' ? valueA - valueB : valueB - valueA;
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return order === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      }

      return 0;
    }
    return 0;
  });

  // Mobile: compact standings with expandable rows
  if (isMobile) {
    return (
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="standings">
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ padding: '8px', width: '30px' }}>#</StyledTableCell>
              <StyledTableCell sx={{ padding: '8px' }}>Name</StyledTableCell>
              <StyledTableCell align="right" sx={{ padding: '8px' }}>
                <TableSortLabel
                  active={orderBy === 'totalPoints'}
                  direction={orderBy === 'totalPoints' ? order : 'asc'}
                  onClick={() => handleRequestSort('totalPoints')}
                >
                  Points
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell sx={{ padding: '4px', width: '30px' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row, index) => (
              <MobileRow key={row.name} row={row} rank={index + 1} displayName={userDisplayNames.get(row.name)} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Desktop: full table (unchanged)
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table sx={{ minWidth: 700 }} aria-label="customized table">
        <TableHead>
          <TableRow>
            <StyledTableCell align="left">
              Username
            </StyledTableCell>
            <StyledTableCell align="left">
              <TableSortLabel
                active={orderBy === 'pickScorePoints'}
                direction={orderBy === 'pickScorePoints' ? order : 'asc'}
                onClick={() => handleRequestSort('pickScorePoints')}
              >
                Pick Score Points
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell align="left">
              <TableSortLabel
                active={orderBy === 'pickWinnerPoints'}
                direction={orderBy === 'pickWinnerPoints' ? order : 'asc'}
                onClick={() => handleRequestSort('pickWinnerPoints')}
              >
                Pick Winner Points
              </TableSortLabel>
            </StyledTableCell>
            <StyledTableCell align="left">
              <TableSortLabel
                active={orderBy === 'totalPoints'}
                direction={orderBy === 'totalPoints' ? order : 'asc'}
                onClick={() => handleRequestSort('totalPoints')}
              >
                Total Points
              </TableSortLabel>
            </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row) => (
            <StyledTableRow key={row.name}>
              <StyledTableCell align="left">{getDisplayName(userDisplayNames.get(row.name), row.name)}</StyledTableCell>
              <StyledTableCell align="left">{row.pickScorePoints}</StyledTableCell>
              <StyledTableCell align="left">{row.pickWinnerPoints}</StyledTableCell>
              <StyledTableCell align="left">{row.totalPoints}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default React.memo(LeaderboardTable);
